import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import Sidebar from '../../../common/Sidebar/Sidebar';
import {
  UpdateCarProtection,
  getCarProtectionListing,
  deleteMediaBanner,
  deleteMediaBannerBatch,
  setMainBanner,
  reorderBannerMedia,
} from './../../../../Redux/Actions/UserAction';
import { useTranslation } from 'react-i18next';
import { CommonBusinessHeader } from '../CommonBusinessHeader';

const API = 'https://api.carsavenue.com/api';
const uploadConfig = () => ({
  headers: {
    'Content-Type': 'multipart/form-data',
    Authorization: sessionStorage.getItem('loged_In_auth'),
  },
});

const CarProtectionBanner = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const home = useSelector((state) => state?.home);
  const list = home?.getCarProtectionList;
  const banners = list?.[0]?.businessMedia || [];
  const bid = sessionStorage.getItem('BusinessId');

  const refetch = () => dispatch(getCarProtectionListing(bid));

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Upload images and attach them as gallery banners, then refresh.
  const onUpload = async (files) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('media', f));
    const res = await axios.post(API + '/media/new', formData, uploadConfig());
    const ids = (res?.data?.response?.media || []).map((m) => m.mediaId);
    if (ids.length) {
      dispatch(UpdateCarProtection({ mediaIds: { BANNER: ids } }));
      setTimeout(refetch, 700);
    }
  };

  const onSetMain = (businessMediaId) =>
    dispatch(setMainBanner(businessMediaId, () => setTimeout(refetch, 300)));

  const onDelete = (businessMediaId) =>
    dispatch(deleteMediaBanner(businessMediaId));

  // ===== Inline banner UI state (preview + reorder + multi-select delete) =====
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [order, setOrder] = useState([]); // local display order of businessMediaIds
  const [selected, setSelected] = useState([]); // businessMediaIds checked for delete
  const dragId = useRef(null);

  // Only MAIN_BANNER / BANNER entries, main first.
  const bannerList = (banners || [])
    .filter((b) => b?.mediaType === 'MAIN_BANNER' || b?.mediaType === 'BANNER')
    .sort((a, b) => {
      // Main banner always first, then by saved priority (ascending).
      const mainDiff =
        (a?.mediaType === 'MAIN_BANNER' ? 0 : 1) -
        (b?.mediaType === 'MAIN_BANNER' ? 0 : 1);
      if (mainDiff !== 0) return mainDiff;
      const pa = a?.priority ?? Number.MAX_SAFE_INTEGER;
      const pb = b?.priority ?? Number.MAX_SAFE_INTEGER;
      return pa - pb;
    });

  // Keep local order in sync with fetched banners (append new, drop removed).
  useEffect(() => {
    const ids = bannerList.map((b) => b?.businessMediaId);
    setOrder((prev) => {
      const kept = prev.filter((id) => ids.includes(id));
      const added = ids.filter((id) => !kept.includes(id));
      return [...kept, ...added];
    });
    setSelected((prev) => prev.filter((id) => ids.includes(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banners]);

  const orderedBanners = order
    .map((id) => bannerList.find((b) => b?.businessMediaId === id))
    .filter(Boolean);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    try {
      setUploading(true);
      await onUpload(files);
    } finally {
      setUploading(false);
    }
  };

  const onDragStart = (id) => (dragId.current = id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetId) => {
    const src = dragId.current;
    dragId.current = null;
    if (!src || src === targetId) return;
    setOrder((prev) => {
      const next = [...prev];
      const from = next.indexOf(src);
      const to = next.indexOf(targetId);
      if (from < 0 || to < 0) return prev;
      next.splice(from, 1);
      next.splice(to, 0, src);
      // Persist the new banner order to the backend.
      dispatch(reorderBannerMedia(next));
      return next;
    });
  };

  const toggleSelect = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const deleteSelected = () => {
    // [FB2-#4] Delete ALL selected banners in one batch (one confirm, one
    // refresh) instead of firing a per-item Swal+reload that killed the rest.
    dispatch(
      deleteMediaBannerBatch(selected, () => {
        setSelected([]);
        setTimeout(refetch, 300);
      })
    );
  };

  const sideBarOpt = [
    {
      icon: 'assets/images/icons/sp-business/manage-business.svg',
      link: '/sp-car-protection-business',
      label: t('Manage Business'),
    },
    {
      icon: 'assets/images/icons/sp-business/manage-service.svg',
      link: '/sp-car-protection-packages',
      label: t('Manage Packages'),
    },
    {
      icon: 'assets/images/icons/sp-business/banner.svg',
      link: '/sp-car-protection-banner',
      label: t('Banner'),
    },
    {
      icon: 'assets/images/icons/sp-business/manage-working-hours.svg',
      link: '/sp-car-protection-working-hours',
      label: t('Manage Working Hours'),
    },
  ];

  return (
    <div>
      <CommonBusinessHeader dataList={list} getNameKey="carProtectionName" />
      <div className="main-content-area">
        <div className="container">
          <div className="row justify-content-between">
            <Sidebar options={sideBarOpt} />
            <div className="col-12 col-sm-12 col-md-9 col-lg-9">
              {/* ===== Inline Banner Manager (preview + reorder + multi-delete) ===== */}
              <h2 className="mb-2">{t('Manage Banner')}</h2>
              <p className="text-muted mb-3">
                {t(
                  'Upload your banner images, click the star to set the main banner, drag thumbnails to reorder, or select several and delete them together.'
                )}
              </p>

              {/* Upload area */}
              <div
                onClick={() => inputRef.current && inputRef.current.click()}
                style={{
                  border: '2px dashed #f5a623',
                  borderRadius: '10px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: '#fff9ed',
                }}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFiles}
                  style={{ display: 'none' }}
                />
                <div style={{ fontSize: '2rem' }}>{uploading ? '⏳' : '☁️'}</div>
                <div>
                  {uploading
                    ? t('Uploading…')
                    : t('Click to upload banner images (up to 10)')}
                </div>
              </div>

              {/* Multi-select delete toolbar */}
              {selected.length > 0 && (
                <div className="d-flex align-items-center mt-3">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={deleteSelected}
                  >
                    🗑 {t('Delete selected')} ({selected.length})
                  </button>
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => setSelected([])}
                  >
                    {t('Clear selection')}
                  </button>
                </div>
              )}

              {/* Grid of banners */}
              {orderedBanners.length > 0 ? (
                <div className="row mt-3">
                  {orderedBanners.map((b) => {
                    const id = b?.businessMediaId;
                    const isMain = b?.mediaType === 'MAIN_BANNER';
                    const isChecked = selected.includes(id);
                    return (
                      <div
                        key={id}
                        className="col-6 col-sm-6 col-md-4 col-lg-3 mb-4"
                        draggable
                        onDragStart={() => onDragStart(id)}
                        onDragOver={onDragOver}
                        onDrop={() => onDrop(id)}
                      >
                        <div
                          style={{
                            position: 'relative',
                            border: isMain
                              ? '3px solid #f5a623'
                              : '1px solid #ddd',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            cursor: 'move',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSelect(id)}
                            style={{
                              position: 'absolute',
                              top: 6,
                              left: 6,
                              zIndex: 2,
                              width: 18,
                              height: 18,
                            }}
                          />
                          <img
                            src={b?.media?.path}
                            alt=""
                            style={{
                              width: '100%',
                              height: '8rem',
                              objectFit: 'cover',
                              display: 'block',
                            }}
                          />
                          {isMain && (
                            <span
                              style={{
                                position: 'absolute',
                                top: 6,
                                right: 6,
                                background: '#f5a623',
                                color: '#fff',
                                fontSize: '0.7rem',
                                padding: '2px 6px',
                                borderRadius: '4px',
                              }}
                            >
                              ★ {t('Main')}
                            </span>
                          )}
                          <div
                            className="d-flex justify-content-between"
                            style={{ padding: '4px 6px', background: '#fafafa' }}
                          >
                            {!isMain ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0"
                                title={t('Set as main')}
                                onClick={() => onSetMain(id)}
                              >
                                ★ {t('Set as main')}
                              </button>
                            ) : (
                              <span />
                            )}
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0 text-danger"
                              title={t('Delete')}
                              onClick={() => onDelete(id)}
                            >
                              🗑
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted mt-4">
                  {t('No banners uploaded yet.')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarProtectionBanner;
